import dayjs from 'dayjs';
import { isEqual, cloneDeep, without } from 'lodash';
import Popper from 'vue-popperjs';
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import kapiche_mark_inverse from 'assets/img/dashboards/kapiche_mark_inverse.svg';
import dashboard_chevron_right from 'assets/img/dashboards/dashboard-chevron-right.svg';
import ErrorBanner from 'components/widgets/ErrorBanner.vue';
import ControlBar from 'components/widgets/FilterBar/ControlBar.vue';
import Modal from 'components/widgets/Modal.vue';
import { BfModal, BfDialog, BfTextInput, BfButton } from 'components/Butterfly';
import { extend, ValidationObserver, ValidationProvider } from 'vee-validate';
import PageLoader from 'components/widgets/PageLoader.vue';
import Query from 'src/api/query';
import QueryUtils from 'src/utils/query';
import Util from 'src/utils/general';
import { LOAD_PROJECT, LOAD_ANALYSIS, LOAD_DASHBOARD, UPDATE_DASHBOARD, FETCH_APPROVED_DOMAINS, SET_DASHBOARD_QUERIES, SET_ERRORS, SET_DASHBOARD, CLEAR_PROJECT, SET_WIDGET_CONFIG, DISCARD_DASHBOARD_CONFIG_CHANGES, SAVE_DASHBOARD_DATE_RANGE, } from 'src/store/types';
import DashboardSharingModal from 'components/project/analysis/results/dashboards/DashboardSharingModal.vue';
import EmailDigestManagementModal from 'components/widgets/EmailDigest/EmailDigestManagementModal.vue';
import { ResizeSensor } from 'css-element-queries/src/ResizeSensor';
import { generateConfig, processQueries, widgetNameToLabel } from './Dashboard.utils';
import ProjectAPI from 'src/api/project';
import DashboardWidgetsModal from "components/widgets/DashboardWidgetsModal/DashboardWidgetsModal.vue";
import ModalConfirmPrompt from "components/widgets/ModalConfirmPrompt/ModalConfirmPrompt.vue";
import { DateRangeTypeEnum } from 'types/DashboardTypes';
import DashboardChangesTooltip from "components/widgets/DashboardChangesTooltip/DashboardChangesTooltip.vue";
import { QueryLocation } from 'src/types/Query.types';
import Icon from 'src/components/Icon.vue';
import FilterBar from 'src/components/project/analysis/results/ThemeBuilder/FilterBar.vue';
import { applyToggledSegmentsToQueryRows } from 'src/types/utils';
import SliceRename from './SliceRename.vue';
import { dateRangeToFilter } from 'src/utils/dates';
import { isQueryValid } from 'src/components/project/analysis/results/query/utils';
// field max length validator: eg. `rules="max:200"`
extend('max', (value, max) => {
    return value.length > max ? `Your text is too long (${value.length}).   Maximum length is ${max}.` : true;
});
export default Vue.extend({
    components: {
        DashboardChangesTooltip,
        DashboardWidgetsModal,
        ModalConfirmPrompt,
        ErrorBanner,
        Modal,
        PageLoader,
        Popper,
        DashboardSharingModal,
        ControlBar,
        BfModal,
        BfDialog,
        BfTextInput,
        BfButton,
        ValidationObserver,
        ValidationProvider,
        EmailDigestManagementModal,
        Icon,
        FilterBar,
        SliceRename,
    },
    provide: {
        isOnDashboard: true,
    },
    props: {
        projectId: { type: Number, required: false, default: null },
        analysisId: { type: Number, required: false, default: null },
        dashboardId: { type: [Number, String], required: true },
        inAnalysis: { type: Boolean, required: true },
        queryId: { type: Number, required: false, default: null },
    },
    data() {
        return {
            dashboardType: 'overview',
            currentConcepts: [],
            queryName: undefined,
            kapiche_mark_inverse,
            dashboard_chevron_right,
            loaded: false,
            availableQueries: [],
            usedQueries: [],
            projectMembers: [],
            dashboardMembers: [],
            stagedFilters: [],
            shareModalVisible: false,
            digestModalVisible: false,
            digestManagementModalVisible: false,
            dashboardError: null,
            isEditDashboardFormUpdating: false,
            editDashboardModalVisible: false,
            editDashboardFormValues: {
                name: null,
            },
            isDeleteDashboardPending: false,
            deleteDashboardModalVisible: false,
            saveAsModalVisible: false,
            saveAsFormValues: {
                name: null,
            },
            isSaveAsFormUpdating: false,
            dashboardWidgetsModalVisible: false,
            visibleDiscardAllModal: false,
            groupBy: '',
            speakerField: '',
            selectedQueryRows: [],
            selectedCompareQueryRows: [],
            location: QueryLocation.Dashboard,
            editSliceOneName: false,
            compareMode: false,
        };
    },
    computed: {
        ...mapGetters([
            'loggedIn',
            'currentSite',
            'currentModel',
            'currentDashboard',
            'currentAnalysis',
            'defaultDateField',
            'dashboardAwaiting',
            'sortedSegmentsForFieldsLimited',
            'approved_domains',
            'approved_domains_loading',
            'featureFlags',
            'dashboardQueries',
            'currentUser',
            'savedQueries',
            'hasNPS',
            'hasSentiment',
            'dashboardWidgetConfig',
            'dashboardDateRange',
            'hasNumericFields',
            'hasDate',
        ]),
        dashboardDateRangeFilters() {
            return dateRangeToFilter(this.dashboardDateRange?.dateField ?? '', this.dashboardDateRange?.type ?? DateRangeTypeEnum.ALL_TIME, this.dashboardDateRange?.dateFrom ?? '', this.dashboardDateRange?.dateTo ?? '');
        },
        validQueryRows() {
            if (this.selectedQueryRows.length && !isQueryValid(this.selectedQueryRows))
                return [];
            return QueryUtils.convertBotanicQueriesToDashboardFilters(this.selectedQueryRows);
        },
        validQueryRowsCompare() {
            if (this.selectedCompareQueryRows.length && !isQueryValid(this.selectedCompareQueryRows))
                return [];
            return QueryUtils.convertBotanicQueriesToDashboardFilters(this.selectedCompareQueryRows);
        },
        outsideDateFilters() {
            const topLevelDateField = this.dashboardDateRangeFilters[0]?.field;
            let otherDateFilters = this.validQueryRows.slice();
            if (this.compareMode) {
                otherDateFilters = otherDateFilters.concat(this.validQueryRowsCompare);
            }
            otherDateFilters = otherDateFilters.filter((row) => row.field === topLevelDateField);
            if (!otherDateFilters.length) {
                return [];
            }
            const outsideFilters = [];
            const minPrimaryDate = dayjs(this.dashboardDateRangeFilters[0].value);
            const maxPrimaryDate = dayjs(this.dashboardDateRangeFilters[1].value);
            otherDateFilters.forEach((filter) => {
                const date = dayjs(filter.value);
                if (date.isBefore(minPrimaryDate) || date.isAfter(maxPrimaryDate)) {
                    outsideFilters.push(filter);
                }
            });
            return outsideFilters;
        },
        filterWarnings() {
            return this.outsideDateFilters.map((filter) => {
                return {
                    field: filter.field,
                    value: filter.value,
                    text: 'This date falls outside the top-level Dashboard Date filter.',
                };
            });
        },
        viewedQueryNotOnDashboard() {
            return this.queryId && !this.usedQueries.includes(this.queryId);
        },
        routeToOverview() {
            return this.inAnalysis ? {
                name: 'analysis-dashboard-overview',
                projectId: this.projectId,
                analysisId: this.analysisId,
                dashboardId: this.dashboardId,
            } : {
                name: 'dashboard-overview',
                params: {
                    dashboardId: this.dashboardId
                }
            };
        },
        /**
         differences between current widget, queries, & filters and that in the saved dashboard
         configuration are considered to be unsaved and so save buttons should be shown
         */
        unsavedChanges() {
            return this.loaded && (this.unsavedSegmentFilters ||
                this.unsavedDateFilters ||
                this.unsavedWidgets ||
                this.unsavedQueries ||
                this.unsavedGroupBy ||
                this.unsavedSpeakerField ||
                this.unsavedCompareMode);
        },
        unsavedWidgets() {
            return !isEqual(this.currentDashboard.config?.widgets, this.dashboardWidgetConfig);
        },
        unsavedSegmentFilters() {
            const saved = this.currentDashboard.config?.queryRows;
            const unsaved = this.selectedQueryRows;
            const compareSaved = this.currentDashboard.config?.compareQueryRows;
            const compareUnsaved = this.selectedCompareQueryRows;
            return !isEqual(saved, unsaved) || !isEqual(compareSaved, compareUnsaved);
        },
        unsavedDateFilters() {
            return !isEqual(this.currentDashboard.config?.dateRange, this.dashboardDateRange);
        },
        unsavedQueries() {
            return !isEqual(this.currentDashboard.queries.map(q => q.name), this.dashboardQueries.map(q => q.name));
        },
        unsavedGroupBy() {
            return this.groupBy !== this.currentDashboard?.groupby_field;
        },
        unsavedSpeakerField() {
            return this.speakerField !== this.currentDashboard?.speaker_field;
        },
        unsavedCompareMode() {
            return this.compareMode !== !!this.currentDashboard?.config?.compareMode;
        },
        dateFields() {
            return (this.currentModel && this.currentModel.dateFields) || [];
        },
        dateFieldNames() {
            return this.dateFields.map(f => f.name);
        },
        shareableLink() {
            if (!this.inAnalysis)
                return null;
            return `https://${window.location.host}/${this.currentSite.domain}/dashboards/${this.currentDashboard.url_code}`;
        },
        selectedQueryCount() {
            const ret = this.currentDashboard?.include_other === true ? 1 : 0;
            return ret + this.dashboardQueries.length;
        },
        editDashboardInitialFormValues() {
            return {
                name: this.currentDashboard?.name ?? '',
            };
        },
        saveAsInitialFormValues() {
            return {
                name: '',
            };
        },
        usersWithAccess() {
            return this.dashboardMembers.length + this.projectMembers.length;
        },
        requiredStateIsAvailable() {
            return (!!this.currentDashboard?.id
                && !!this.currentSite
                && !!this.currentModel
                && !!this.currentAnalysis);
        },
        analystUser() {
            return !!!this.currentUser?.viewer;
        },
        groupByDiff() {
            if (!this.currentDashboard || !this.unsavedGroupBy) {
                return null;
            }
            return {
                original: this.currentDashboard.groupby_field || 'None',
                current: this.groupBy || 'None',
            };
        },
        speakerFieldDiff() {
            if (!this.currentDashboard || !this.unsavedSpeakerField) {
                return null;
            }
            return {
                original: this.currentDashboard.speaker_field || 'None',
                current: this.speakerField || 'None',
            };
        },
        dateRangeDiff() {
            return {
                original: this.currentDashboard.config?.dateRange,
                current: this.dashboardDateRange
            };
        },
        themesDiff() {
            const currentThemes = this.dashboardQueries?.map(q => q.name);
            const originalThemes = this.currentDashboard?.queries?.map(q => q.name);
            if (!currentThemes || !originalThemes) {
                return {
                    added: [],
                    removed: []
                };
            }
            return {
                added: without(currentThemes, ...originalThemes),
                removed: without(originalThemes, ...currentThemes)
            };
        },
        filterDiff() {
            const map = (rows) => rows?.flatMap((f) => f?.values.map((v) => `${f.field}: ${v}`) ?? []) ?? [];
            const currentFilters = map(this.selectedQueryRows);
            const originalFilters = map(this.currentDashboard?.config?.queryRows);
            const currentCompareFilters = map(this.selectedCompareQueryRows);
            const originalCompareFilters = map(this.currentDashboard?.config?.compareQueryRows);
            if (!currentFilters || !originalFilters) {
                return {
                    added: [],
                    removed: [],
                    compareAdded: [],
                    compareRemoved: [],
                };
            }
            return {
                added: without(currentFilters, ...originalFilters),
                removed: without(originalFilters, ...currentFilters),
                compareAdded: without(currentCompareFilters, ...originalCompareFilters),
                compareRemoved: without(originalCompareFilters, ...currentCompareFilters),
            };
        },
        compareModeNewValue() {
            if (this.compareMode !== !!this.currentDashboard?.config?.compareMode) {
                return this.compareMode;
            }
            return undefined;
        },
        compareModeView() {
            return this.compareMode && this.dashboardType === 'overview';
        }
    },
    watch: {
        dashboardId(old_id, new_id) {
            if (old_id !== new_id)
                this.loadDashboard(this.dashboardId);
        },
        editDashboardInitialFormValues: {
            immediate: true,
            handler(values) {
                // apply new initial values to formValues as they become available
                Object.assign(this.editDashboardFormValues, values);
            }
        },
        currentDashboard(newVal, oldVal) {
            if (newVal && newVal.id !== oldVal?.id) {
                this.groupBy = newVal.groupby_field;
                this.speakerField = newVal.speaker_field;
            }
        }
    },
    beforeDestroy: function () {
        this.$store.commit(SET_DASHBOARD, null);
        // Cleanup click handler for dropdown behaviour
        document.removeEventListener('click', this.onDocumentClick);
    },
    async mounted() {
        this.whenInIFrame();
        // load rest of data
        this.loadDashboard(this.dashboardId);
    },
    methods: {
        ...mapActions({
            FETCH_APPROVED_DOMAINS,
            LOAD_DASHBOARD,
            UPDATE_DASHBOARD,
            SET_WIDGET_CONFIG,
            DISCARD_DASHBOARD_CONFIG_CHANGES,
            SAVE_DASHBOARD_DATE_RANGE
        }),
        widgetConfigDiff(view) {
            return this.dashboardWidgetConfig[view]
                .reduce((diffList, curr) => {
                const original = this.currentDashboard.config.widgets[view].find((w) => w.name === curr.name);
                if (!isEqual(original, curr)) {
                    let diff = {
                        name: widgetNameToLabel(curr.name),
                    };
                    if (curr?.visible !== original?.visible)
                        diff['toVisible'] = curr.visible;
                    return diffList.concat(diff);
                }
                else {
                    return diffList;
                }
            }, []);
        },
        toggleDiscardAllModal(visible) {
            this.visibleDiscardAllModal = visible;
        },
        discardDashboardChanges() {
            this.toggleDiscardAllModal(false);
            this.DISCARD_DASHBOARD_CONFIG_CHANGES();
            this.selectedQueryRows = this.currentDashboard.config?.queryRows ?? [];
            this.selectedCompareQueryRows = this.currentDashboard.config?.compareQueryRows ?? [];
            this.groupBy = this.currentDashboard.groupby_field;
            this.speakerField = this.currentDashboard.speaker_field;
            this.compareMode = this.currentDashboard.config?.compareMode ?? false;
        },
        resetAllWidgetConfig() {
            const widgets = cloneDeep(this.dashboardWidgetConfig);
            [...widgets.overview, ...widgets.drilldown]
                .forEach((widget) => {
                widget.visible = true;
                widget.options = {};
            });
            this.$store.commit(SET_WIDGET_CONFIG, { widgets });
        },
        resetDashboard() {
            this.resetAllWidgetConfig();
            this.setDateRange(DateRangeTypeEnum.ALL_TIME, this.defaultDateField, '', '');
            this.selectAllQueries();
            this.applyQueries();
            this.selectedQueryRows = [];
            this.selectedCompareQueryRows = [];
        },
        setDashboardType(dashboardType, names = {}) {
            this.dashboardType = dashboardType;
            this.currentConcepts = names['concepts'];
            this.queryName = names['query'];
        },
        async loadDashboard(dashboardId) {
            // In the viewer URL, the dashboard ID is hashed for security purposes.
            // The URL might look something like this:
            //
            //     http://localhost:8080/default/dashboards/YEglYOpn/overview
            //
            // The hash appears in the same position in the URL that an (DB)
            // dashboard ID normally would.
            // It has been possible to reach here trying to load a dashboard
            // from a different project (and analysis) that is currently loaded
            // in the vuex store. The check is made below to detect that
            // situation and clear the project on the store if necessary.
            const dashboardInCurrentAnalysis = this.currentAnalysis?.dashboards?.find(o => o.id === dashboardId);
            if (!dashboardInCurrentAnalysis) {
                // The requested dashboard is not in the currently-loaded analysis. This means
                // we must invalidate our existing store state for project and everything
                // beneath it.
                this.$store.commit(CLEAR_PROJECT);
            }
            this.loaded = false;
            this.dashboardError = undefined;
            try {
                if (this.inAnalysis) {
                    // ANALYST MODE
                    await this.loadInAnalystMode(dashboardId);
                }
                else { // ELSE VIEWER MODE
                    await this.loadInViewerMode(dashboardId);
                }
                this.selectedQueryRows = this.currentDashboard.config?.queryRows ?? [];
                this.selectedCompareQueryRows = this.currentDashboard.config?.compareQueryRows ?? [];
                this.compareMode = this.currentDashboard.config?.compareMode ?? false;
            }
            catch (error) {
                this.dashboardError = error;
                console.warn(error);
            }
            finally {
                this.loaded = true;
            }
        },
        saveDashboard() {
            const dashboard = {
                id: this.currentDashboard.id,
                analysis_id: this.analysisId,
                name: this.currentDashboard.name,
                queries: processQueries(this.dashboardQueries),
                config: generateConfig(this.dashboardWidgetConfig, this.dashboardDateRange, this.selectedQueryRows, this.selectedCompareQueryRows, this.compareMode),
                groupby_field: this.groupBy,
                speaker_field: this.speakerField,
            };
            this.$store.dispatch({ type: UPDATE_DASHBOARD, dashboard, rethrowErrors: true });
            this.$analytics.track.dashboard.save(this.currentDashboard.id);
        },
        toggleFilters(filters) {
            const rows = applyToggledSegmentsToQueryRows(filters, this.selectedQueryRows);
            this.selectedQueryRows = rows;
        },
        updateDashboardMembers(payload) {
            Vue.set(this, 'dashboardMembers', payload);
        },
        speakerFieldChange(field) {
            this.speakerField = field;
        },
        async loadInViewerMode(dashboardCode) {
            // After this successfully completes, the currentDashboard should
            // be the same one this dashboardCode refers to.
            await this.LOAD_DASHBOARD({ dashboardId: dashboardCode, rethrowErrors: true, loadConfig: true, isViewer: true });
            // After this loads, currentAnalysis should be defined.
            await this.loadAnalysis();
        },
        async loadInAnalystMode(dashboardId) {
            const prevDashboard = this.currentDashboard;
            let responses = await Promise.all([
                this.LOAD_DASHBOARD({ dashboardId: dashboardId, analysisId: this.analysisId, projectId: this.projectId, rethrowErrors: true, loadConfig: true, isViewer: false }),
                Query.listSavedQueries(this.projectId, this.analysisId),
                ProjectAPI.getDashboardMembers(dashboardId),
                ProjectAPI.getProjectMembers(this.projectId),
                this.FETCH_APPROVED_DOMAINS()
            ]);
            this.availableQueries = responses[1].slice();
            this.usedQueries = this.dashboardQueries.map(q => q.id);
            this.dashboardMembers = responses[2].slice();
            this.projectMembers = responses[3].slice();
            // If we DON'T have the analysis or the current analysis does NOT
            // match the required analysis for the dashboard. The case where
            // the previous dashboard is null, is when a user deletes a dashboard.
            // We need to refetch the list of dashboards, so we reload the analysis.
            if (this.analysisIsMissingOrStale() || prevDashboard === null) {
                await this.loadAnalysis();
            }
        },
        analysisIsMissingOrStale() {
            return this.currentAnalysis === null ||
                this.currentModel === null ||
                this.currentDashboard.analysis.id !== this.currentAnalysis.id;
        },
        async loadAnalysis() {
            await this.$store.dispatch({
                type: LOAD_ANALYSIS,
                projectId: this.currentDashboard.project.id,
                analysisId: this.currentDashboard.analysis.id
            });
        },
        async loadProject() {
            await this.$store.dispatch({
                type: LOAD_PROJECT,
                projectId: this.currentDashboard.project.id
            });
        },
        showQueriesModal() {
            this.usedQueries = this.dashboardQueries.map(q => q.id);
            this.$refs.queriesModal.show();
        },
        hideQueriesModal(event, resetSelections = true) {
            this.$refs.queriesModal.hide();
            if (resetSelections) {
                this.usedQueries = this.dashboardQueries.map(q => q.id);
            }
        },
        selectAllQueries() {
            this.usedQueries = this.availableQueries.map(q => q.id);
        },
        deselectAllQueries() {
            this.usedQueries = this.usedQueries.filter(q => q === this.queryId);
        },
        applyQueries() {
            const selectedQueries = processQueries(this.availableQueries.filter((q) => this.usedQueries.includes(q.id)));
            this.$store.commit(SET_DASHBOARD_QUERIES, selectedQueries);
            this.hideQueriesModal(null, false);
        },
        showExplorerMode() {
            this.$router.push({
                name: 'analysis-dashboard-overview',
                params: {
                    projectId: this.currentDashboard?.project.id,
                    analysisId: this.currentDashboard?.analysis.id,
                    dashboardId: this.currentDashboard?.id,
                }
            });
            this.$analytics.track.dashboard.explorerMode(this.currentDashboard?.id);
        },
        showShareModal() {
            this.shareModalVisible = true;
        },
        showWidgetConfigModal() {
            this.$analytics.track.dashboard.customization.showWidgetsModal(this.currentDashboard.id);
            this.dashboardWidgetsModalVisible = true;
        },
        showDigestModal() {
            this.$analytics.track.dashboard.digest.modalOpened(this.currentDashboard.id);
            this.digestManagementModalVisible = true;
        },
        hideShareModal() {
            this.shareModalVisible = false;
        },
        // ================================================================ DASHBOARD EDITING
        showEditDashboardModal() {
            this.editDashboardModalVisible = true;
        },
        resetEditDashboardForm() {
            Object.assign(this.editDashboardFormValues, this.editDashboardInitialFormValues);
        },
        closeEditDashboardModal() {
            this.resetEditDashboardForm();
            this.editDashboardModalVisible = false;
        },
        async saveEditDashboard() {
            const { name } = this.editDashboardFormValues;
            try {
                this.isEditDashboardFormUpdating = true;
                await this.UPDATE_DASHBOARD({
                    dashboard: {
                        id: this.currentDashboard.id,
                        analysis_id: this.currentAnalysis.id,
                        name,
                    },
                    rethrowErrors: true,
                });
                // update dashboards list
                await this.loadAnalysis();
                // close modal
                this.closeEditDashboardModal();
            }
            catch (resp) {
                if (resp.status === 400) {
                    const errors = await resp.json();
                    // pass errors to form context to highlight each input's errors
                    this.$refs.editDashboardObserver.setErrors(errors);
                }
            }
            finally {
                this.isEditDashboardFormUpdating = false;
            }
        },
        // ================================================================ DASHBOARD DELETING
        showDeleteDashboardModal() {
            this.deleteDashboardModalVisible = true;
        },
        closeDeleteDashboardModal() {
            this.deleteDashboardModalVisible = false;
        },
        async deleteDashboard() {
            try {
                this.isDeleteDashboardPending = true;
                const newId = this.currentAnalysis.dashboards.find(({ id }) => id !== this.currentDashboard.id)?.id;
                await ProjectAPI.deleteDashboard(this.currentDashboard.id);
                this.$store.commit(SET_DASHBOARD, null);
                // close modal before changing Dashboards
                this.deleteDashboardModalVisible = false;
                // load first Dashboard in current analysis
                this.$router.push({
                    name: 'analysis-dashboard-overview',
                    params: {
                        ...this.$route.params,
                        dashboardId: newId,
                    },
                });
            }
            catch (errors) {
                // close modal (handle error generally)
                this.closeDeleteDashboardModal();
                this.$store.dispatch({ type: SET_ERRORS, errors });
            }
            finally {
                this.isDeleteDashboardPending = false;
            }
        },
        showSaveAsModal() {
            this.saveAsModalVisible = true;
        },
        resetSaveAsDashboardForm() {
            Object.assign(this.saveAsFormValues, this.saveAsInitialFormValues);
        },
        closeSaveAsModal() {
            this.resetSaveAsDashboardForm();
            this.saveAsModalVisible = false;
        },
        async saveNewDashboard() {
            const { name } = this.saveAsFormValues;
            try {
                this.isSaveAsFormUpdating = true;
                const res = await ProjectAPI.createDashboard({
                    name,
                    analysis: this.analysisId,
                    queries: this.dashboardQueries.map(({ id }) => ({ id })),
                    slice1_name: this.currentDashboard.slice1_name,
                    slice2_name: this.currentDashboard.slice2_name,
                    config: {
                        dateRange: this.dashboardDateRange,
                        widgets: this.dashboardWidgetConfig,
                        queryRows: this.selectedQueryRows,
                        compareQueryRows: this.selectedCompareQueryRows,
                        compareMode: this.compareMode,
                    }
                });
                // update dashboards list
                await this.loadAnalysis();
                this.closeSaveAsModal();
                const oldDashboardId = this.currentDashboard.id;
                const newDashboardId = res.body.id;
                this.$router.push({
                    name: 'analysis-dashboard-overview',
                    params: {
                        site: this.$route.params.site,
                        projectId: this.$route.params.projectId,
                        analysisId: this.$route.params.analysisId,
                        dashboardId: newDashboardId,
                    }
                });
                this.$analytics.track.dashboard.saveAs(oldDashboardId, newDashboardId);
            }
            catch (resp) {
                if (resp.status === 400) {
                    const errors = await resp.json();
                    // pass errors to form context to highlight each input's errors
                    this.$refs.saveAsObserver.setErrors(errors);
                }
            }
            finally {
                this.isSaveAsFormUpdating = false;
            }
        },
        setDateRange(type, dateField, to, from) {
            this.SAVE_DASHBOARD_DATE_RANGE({ dateRange: {
                    type: type,
                    dateField,
                    dateTo: to,
                    dateFrom: from
                } });
        },
        onWidgetConfigChange(config) {
            this.$analytics.track.dashboard.customization.widgetConfigApplied(this.currentDashboard.id, config);
            this.$store.commit(SET_WIDGET_CONFIG, { widgets: config });
            this.dashboardWidgetsModalVisible = false;
            this.triggerLayout();
        },
        onLinkCopy() {
            this.$analytics.track.dashboard.linkCopied(this.currentDashboard.id);
        },
        // Hide the dropdown menu
        onDocumentClick() {
            this.stagedFilters = [];
            let el = this.$el.querySelector('.right.menu .dropdown .menu');
            if (el) {
                el.classList.remove('visible');
            }
        },
        hasUnstructuredData(query) {
            // FIXME: when https://github.com/Kapiche/botanic/pull/3661 merges
            return QueryUtils.botanicToQueryRows(query).find(i => ['text', 'query'].includes(i.type)) !== undefined;
        },
        whenInIFrame() {
            if (top !== self) {
                // means we are in an iframe
                const wrapperElement = this.$refs.dashboardWrapper;
                new ResizeSensor(wrapperElement, function () {
                    window.parent.postMessage(JSON.stringify({
                        src: window.location.toString(),
                        context: 'iframe.resize',
                        height: wrapperElement.scrollHeight
                    }), '*');
                });
            }
        },
        scrollToTop() {
            this.$refs.scrollableContent?.scrollTo({
                top: 0,
                left: 0,
                behavior: 'smooth'
            });
        },
        generateSegmentFilterCountLabel: Util.generateSegmentFilterCountLabel,
        triggerLayout() {
            /*
            Each widget listens to resize events to determine when they should update the dimensions of their
            their visualisations.  This normally happens when the browser window is resized.
    
            However there are other circumstances (such as when widget visibility changes) that can affect
            the number of columns and hence the width of each column which will require the widgets to adjust
            their visualisation parameters.
    
            This method lets us dispatch that event and trigger the widget resizing behaviour when necessary.
             */
            window.dispatchEvent(new Event('resize'));
        },
        updateGroupby(value) {
            if (value !== null) {
                this.$analytics.track.dashboard.groupByAggregation(this.currentDashboard.id, value);
            }
            this.groupBy = value;
        },
        syncFilterBarCollapse(collapsed) {
            if (this.$refs.filterBar1)
                this.$refs.filterBar1.collapsed = collapsed;
            if (this.$refs.filterBar2)
                this.$refs.filterBar2.collapsed = collapsed;
        },
        exportCsv() {
            this.$refs.overview?.exportQuery();
        },
        exportPpt() {
            this.$refs.overview?.exportPpt();
        },
        async renameSlice(field, value) {
            await this.UPDATE_DASHBOARD({
                dashboard: {
                    id: this.currentDashboard.id,
                    analysis_id: this.currentAnalysis.id,
                    [field]: value,
                },
                rethrowErrors: true,
            });
        },
    }
});
